import { FiltersCheckboxGroup, FiltersRadioGroup } from '@app/components/products/FiltersBar/FiltersGroup';
import { FilterGroupContainer } from '@app/components/products/FiltersBar/FiltersGroup/FilterGroupContainer';
import { FiltersDeliveryWindows } from '@app/components/products/FiltersBar/FiltersGroup/FiltersDeliveryWindows';
import { EtaFilter } from '@app/components/products/FiltersBar/FiltersGroup/FiltersRadioGroup/EtaFilter';
import { FiltersSwatchesGroup } from '@app/components/products/FiltersBar/FiltersGroup/FiltersSwatchesGroup';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { Filter, ProductFilters } from '@typings';
import React from 'react';

const DELIVERY_WINDOW_FILTER_NAME = 'deliveryWindows';
const CATEGORIES_COLLAPSE_LEVEL = 2;

interface Props {
  attribute: Filter.Field<string>;
  filters: ProductFilters;
  expandedFilters: Record<string, boolean>;
  onFilterChange: (fieldName: string, selectedValues: string | string[]) => void;
  onToggleFilter: (filterField: string, isExpanded: boolean) => void;
}

export const FiltersPanelGroup = ({ attribute, filters, onFilterChange, onToggleFilter, expandedFilters }: Props) => {
  const { isRadio, field, values } = attribute;
  const isSwatchGroup = field === 'swatch.desc' || field === 'sh_swatch.desc';
  const activeFilters = filters[field] ?? [];

  const isExpanded = expandedFilters[field] ?? false;
  const filterValues = filters[field];
  const selectedCount = isDefined(filterValues) && Array.isArray(filterValues) ? filterValues.length : 0;

  const handleSelectedValueChange = React.useCallback(
    (selectedValues: string | string[]) => {
      onFilterChange(field, selectedValues);
    },
    [onFilterChange, field],
  );

  const handleCheckboxGroupValueChange = React.useCallback(
    (selectedValues: string[]) => {
      onFilterChange(field, selectedValues);
    },
    [field, onFilterChange],
  );

  const handleToggle = React.useCallback(() => {
    onToggleFilter(field, isExpanded);
  }, [field, isExpanded, onToggleFilter]);

  const selectedOptions = Array.isArray(activeFilters) ? activeFilters : [activeFilters];

  if (isEmpty(values)) {
    return null;
  }

  if (isRadio) {
    const etaFilterLabel = values.find(({ value }) => value === 'arrivingSoon')?.data;

    return (
      <FilterGroupContainer isExpanded={isExpanded} selectedCount={selectedCount} onToggleExpanded={handleToggle} filterField={field}>
        <FiltersRadioGroup
          fieldOptions={values}
          selectedOption={selectedOptions[0]}
          filterField={field}
          key={field}
          onSelectedValueChange={handleSelectedValueChange}
        />
        {field === 'onlyAvailable' && filters.onlyAvailable === 'arrivingSoon' && <EtaFilter title={etaFilterLabel} />}
      </FilterGroupContainer>
    );
  }

  if (isSwatchGroup) {
    return (
      <FilterGroupContainer isExpanded={isExpanded} selectedCount={selectedCount} onToggleExpanded={handleToggle} filterField={field}>
        <FiltersSwatchesGroup
          fieldOptions={values}
          filterField={field}
          selectedOptions={selectedOptions}
          key={field}
          onSelectedOptionsChange={handleSelectedValueChange}
        />
      </FilterGroupContainer>
    );
  }

  if (field === DELIVERY_WINDOW_FILTER_NAME) {
    return (
      <FilterGroupContainer isExpanded={isExpanded} selectedCount={selectedCount} onToggleExpanded={handleToggle} filterField={field}>
        <FiltersDeliveryWindows
          selectedOptions={selectedOptions}
          onSelectedOptionsChange={handleSelectedValueChange}
          stockType={filters.stockType}
        />
      </FilterGroupContainer>
    );
  }

  return (
    <FilterGroupContainer isExpanded={isExpanded} selectedCount={selectedCount} onToggleExpanded={handleToggle} filterField={field}>
      <FiltersCheckboxGroup
        fieldOptions={values}
        filterField={field}
        selectedOptions={selectedOptions}
        key={field}
        onSelectedOptionsChange={handleCheckboxGroupValueChange}
        collapseLevel={field === 'categories' ? CATEGORIES_COLLAPSE_LEVEL : undefined}
      />
    </FilterGroupContainer>
  );
};
